"use client";

/**
 * Session replay records the page, so it starts only after the visitor says yes
 * to analytics, and stops the moment they take that back.
 *
 * Added late rather than sampled to zero: `replayIntegration` reads
 * `replaysSessionSampleRate` and `replaysOnErrorSampleRate` off the client
 * options while it is being set up, so an integration added after `init` samples
 * exactly like one passed to it. A visitor who withdraws and agrees again gets
 * back the mode they were in, not a fuller one. The rates decide who is
 * recorded, before and after this change alike; what changes is that nobody is
 * recorded before the answer.
 */
import * as Sentry from "@sentry/nextjs";

import { logger } from "../logger";

type ReplayIntegration = ReturnType<typeof Sentry.replayIntegration>;
type RecordingMode = ReturnType<ReplayIntegration["getRecordingMode"]>;

/** Must match the SDK's own integration name, which is how it is looked up. */
const REPLAY_INTEGRATION_NAME = "Replay";

/**
 * One list, so the gated and ungated paths cannot drift on what is masked.
 *
 * `maskAttributes` is written out because the SDK's own default is
 * `["title", "placeholder", "aria-label"]` and stops there, so `alt` reached the
 * recording as written. Our avatars render `alt={name}`, which put a person's
 * name in every replay of every app on this list.
 */
export const REPLAY_OPTIONS = {
  maskAllText: true,
  blockAllMedia: true,
  maskAttributes: ["title", "placeholder", "aria-label", "alt"],
};

/**
 * What a withdrawal interrupted, so a later yes restores the same thing.
 *
 * "session" uploads continuously, "buffer" holds the last minute in case of an
 * error, undefined means the rates left this visitor out. Afterwards the SDK
 * reports all three as not recording, so the answer is kept while it is known.
 */
let interrupted: RecordingMode;

/**
 * The withdrawal's own `stop()`, so a restart waits for it.
 *
 * In session mode `stop()` awaits an upload before it destroys the event buffer
 * and clears the session, so a `start()` landing inside that window is undone
 * by the tail of the stop it raced, and replay is dead until the page reloads.
 */
let stopping: Promise<void> = Promise.resolve();

/**
 * Apply the visitor's analytics answer to session replay.
 *
 * Driven by a boolean rather than an event, so calling it again with the same
 * answer does nothing.
 */
export function applyReplayConsent(granted: boolean): void {
  // No client means `init` never ran. Constructing the integration anyway makes
  // the NEXT construction throw: the SDK latches a module flag and never clears
  // it, so one wasted call costs the real one.
  const client = Sentry.getClient();
  if (!client) return;

  const replay = client.getIntegrationByName<ReplayIntegration>(
    REPLAY_INTEGRATION_NAME,
  );

  if (!granted) {
    if (!replay) return;
    // Only when there is something to interrupt. A second withdrawal reads
    // undefined off an already-stopped recorder, which would erase the mode a
    // later grant needs (a locale switch remounts this and fires one).
    const mode = replay.getRecordingMode();
    if (mode) interrupted = mode;
    // `stop()` also deletes the `sentryReplaySession` entry it wrote, so a no
    // removes what the yes created rather than only stopping the next frame.
    // A refusal the recorder ignored is the one failure this whole file exists
    // to prevent, so it is reported rather than dropped on the floor.
    stopping = replay.stop().catch((error: unknown) => {
      logger.error(
        "Cookie consent: session replay did not stop after a withdrawal",
        error instanceof Error ? error : new Error(String(error)),
      );
    });
    return;
  }

  if (!replay) {
    Sentry.addIntegration(Sentry.replayIntegration({ ...REPLAY_OPTIONS }));
    return;
  }

  // Nothing was interrupted: it is either sampling on its own terms or was
  // never sampled in. Starting it by hand would override the rates.
  if (!interrupted) return;
  const restore = interrupted;
  interrupted = undefined;
  // After the withdrawal's stop has finished, never beside it. The withdrawal
  // threw the sampled session away, so the mode is asked for again, and
  // buffering coming back as buffering keeps the rates honest.
  void stopping.then(() => {
    if (restore === "session") replay.start();
    else replay.startBuffering();
  });
}
